import { Key, useCallback, useMemo, useState } from 'react';
import { isEqual, isEmpty } from 'lodash';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import { ESortDirection, TSortParams } from 'data/types/generic.types';
import useDidUpdate from 'hooks/useDidUpdate';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import {
  TStorageFilters,
  TUseTableParams,
  TUseTableReturnData,
} from '../Table.types';

const useTable = <T>({
  storagePath = '',
  defaultSortColumn = {},
  defaultPageSize = '20',
  defaultColumns,
  defaultFilters = {},
}: TUseTableParams<T>): TUseTableReturnData<T> => {
  const storageFilters = appLocalStorage.getItem(EStorageNames.filters);

  let tableFilters: TStorageFilters<T> = {};
  if (storageFilters) {
    tableFilters = storageFilters[storagePath] as TStorageFilters<T>;
  }
  const [columns, setColumns] = useState(defaultColumns);
  const [page, setPage] = useState(tableFilters?.page || 0);
  const [selectedRows, setSelectedRows] = useState<{
    selectedRowsKeys: Key[];
    selectedRowsRecords: T[];
  }>({
    selectedRowsKeys: [],
    selectedRowsRecords: [],
  });
  const [pageSize, setPageSize] = useState(
    tableFilters?.pageSize || Number(defaultPageSize),
  );
  const [sortColumn, setSortColumn] = useState<TSortParams>(
    tableFilters?.sortColumn || defaultSortColumn,
  );
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, FilterValue | null>
  >(tableFilters?.selectedFilters || defaultFilters);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
  ) => {
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize!);
    }
    if (pagination.current! - 1 !== page) {
      setPage(pagination.current! - 1);
    }
    if (!Array.isArray(sorter)) {
      if (sorter?.order && sorter?.columnKey) {
        setSortColumn({
          sortDirection: ESortDirection[sorter.order],
          sortField: sorter.columnKey as string,
        });
      } else {
        setSortColumn({});
      }
    }
    setSelectedFilters(filters);
  };

  const hasFilters = useMemo(() => {
    return (
      (!isEmpty(defaultSortColumn) &&
        !isEqual(sortColumn, defaultSortColumn)) ||
      (isEmpty(defaultSortColumn) && !isEmpty(sortColumn)) ||
      (!isEmpty(defaultFilters) && !isEqual(selectedFilters, defaultFilters)) ||
      (isEmpty(defaultFilters) && !isEmpty(selectedFilters))
    );
  }, [selectedFilters, sortColumn]);

  useDidUpdate(() => {
    const currentStorageFilters = appLocalStorage.getItem(
      EStorageNames.filters,
    );
    appLocalStorage.setItem(EStorageNames.filters, {
      ...(currentStorageFilters && currentStorageFilters),
      [storagePath]: {
        ...(currentStorageFilters?.[storagePath] &&
          currentStorageFilters[storagePath]),
        page,
        pageSize,
        sortColumn,
        selectedFilters,
      },
    });
  }, [page, pageSize, sortColumn, selectedRows, selectedFilters, storagePath]);

  const derivedColumns = useMemo(
    () =>
      columns.map(item => {
        const filterValue = selectedFilters[item.key as string];
        let sortOrder: 'ascend' | 'descend' | null = null;
        if (sortColumn.sortField === item.key) {
          sortOrder =
            sortColumn.sortDirection === ESortDirection.ascend
              ? 'ascend'
              : 'descend';
        }
        return {
          ...item,
          filteredValue: filterValue || null,
          sortOrder,
        };
      }),
    [columns, selectedFilters, sortColumn],
  );

  const onFiltersReset = useCallback(() => {
    setSelectedFilters(defaultFilters);
    setSortColumn(defaultSortColumn);
    setPage(0);
  }, []);

  return {
    page,
    pageSize,
    sortColumn,
    setPage,
    setPageSize,
    setSortColumn,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter: hasFilters,
    setSelectedRows,
    selectedRows,
    columns: derivedColumns,
    setColumns,
    hasFilters,
    onFiltersReset,
    setSelectedFilters,
  };
};

export default useTable;
